import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import TenantNameLink from '../components/TenantNameLink';
import { floorService, roomService, bedService } from '../services/api';
import { Floor, Room, Bed } from '../types';
import './Dashboard.css';

/** Bed count implied by sharing type (must match backend / schema). */
const SHARING_TO_TOTAL_BEDS: Record<string, number> = {
  PRIVATE: 1,
  '2_BED': 2,
  '3_BED': 3,
  '4_BED': 4,
  '6_BED': 6,
  '8_BED': 8,
};

function totalBedsFromSharingType(sharingType: string): number {
  return SHARING_TO_TOTAL_BEDS[String(sharingType)] ?? 4;
}

/** API only allows AC / NON_AC; legacy rows map to NON_AC for forms. */
function normalizeRoomType(t: string | undefined | null): NonNullable<Room['roomType']> {
  const u = String(t ?? '').toUpperCase();
  return u === 'AC' ? 'AC' : 'NON_AC';
}

function formatRoomTypeLabel(t: string | undefined | null): string {
  return normalizeRoomType(t) === 'AC' ? 'AC' : 'Non-AC';
}

function bedOccupantInfo(bed: Bed): { id: number; name: string } | null {
  const o = bed.occupant as { id?: number; name?: string; username?: string } | undefined;
  if (o?.id == null) return null;
  const name = (o.name || o.username || '').trim();
  if (!name) return null;
  return { id: o.id, name };
}

const RoomBedManagement: React.FC = () => {
  const { hostel } = useAuth();
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'floors' | 'rooms' | 'beds'>(
    'floors'
  );

  const [showFloorForm, setShowFloorForm] = useState(false);
  const [newFloor, setNewFloor] = useState({
    hostelId: hostel?.id || 0,
    floorNumber: '',
  });

  const [showRoomForm, setShowRoomForm] = useState(false);
  const [newRoom, setNewRoom] = useState<{
    hostelId: number;
    floorId: string;
    roomNumber: string;
    roomType: NonNullable<Room['roomType']>;
    sharingType: string;
    totalBeds: string;
    rentPerBed: string;
  }>({
    hostelId: hostel?.id || 0,
    floorId: '',
    roomNumber: '',
    roomType: 'NON_AC',
    sharingType: '4_BED',
    totalBeds: '4',
    rentPerBed: '',
  });

  // Bed creation is disabled; beds are created from rooms (sharing type).

  const [editFloor, setEditFloor] = useState<{
    id: number;
    floorNumber: string;
    description: string;
  } | null>(null);

  const [editRoom, setEditRoom] = useState<{
    id: number;
    floorId: string;
    roomNumber: string;
    roomType: NonNullable<Room['roomType']>;
    sharingType: string;
    totalBeds: string;
    rentPerBed: string;
  } | null>(null);

  const [editBed, setEditBed] = useState<{
    id: number;
    bedNumber: string;
    bedName: string;
    rentPerWeek: string;
    rentPerMonth: string;
  } | null>(null);

  const [floorDeleteModal, setFloorDeleteModal] = useState<Floor | null>(null);
  const [floorDeleteBusy, setFloorDeleteBusy] = useState(false);

  /** Floor accordion in Rooms / Beds tabs (`undefined` or `true` = expanded). */
  const [roomsByFloorOpen, setRoomsByFloorOpen] = useState<Record<number, boolean>>({});
  const [bedsByFloorOpen, setBedsByFloorOpen] = useState<Record<number, boolean>>({});
  /** Beds tab: room accordions under each floor (`undefined` or `true` = expanded). Keys: `room:<id>` or `floorOrphanBeds:<floorId>`. */
  const [bedsByRoomOpen, setBedsByRoomOpen] = useState<Record<string, boolean>>({});

  const ORPHAN_FLOOR_SECTION_ID = -1;

  const roomsOnFloor = (floorId: number) =>
    rooms.filter((r) => Number(r.floorId) === Number(floorId));

  const isBedOccupied = (b: Bed) =>
    String(b.bedStatus || '').toUpperCase() === 'OCCUPIED';

  const isBedActive = (b: Bed) => b.isActive !== false;

  const bedsOnFloor = (floorId: number) => {
    const roomIds = new Set(roomsOnFloor(floorId).map((r) => r.id));
    return beds.filter(
      (b) => Number(b.floorId) === Number(floorId) || roomIds.has(b.roomId)
    );
  };

  const occupiedBedsOnFloor = (floorId: number) =>
    bedsOnFloor(floorId).filter((b) => isBedActive(b) && isBedOccupied(b)).length;

  const roomHasOccupiedBed = (roomId: number) =>
    beds.some(
      (b) =>
        Number(b.roomId) === Number(roomId) &&
        isBedActive(b) &&
        isBedOccupied(b)
    );

  const errMsg = (e: unknown, fallback: string) => {
    const ax = e as { response?: { data?: { message?: string } } };
    return ax?.response?.data?.message || fallback;
  };

  const loadFloors = useCallback(async () => {
    try {
      const data = await floorService.getFloorsByHostel(hostel!.id);
      setFloors(data);
    } catch (error) {
      console.error('Failed to load floors:', error);
    }
  }, [hostel?.id]);

  const loadRooms = useCallback(async () => {
    try {
      const data = await roomService.getRoomsByHostel(hostel!.id);
      setRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  }, [hostel?.id]);

  const loadBeds = useCallback(async () => {
    try {
      const data = await bedService.getBedsByHostel(hostel!.id);
      setBeds(data);
    } catch (error) {
      console.error('Failed to load beds:', error);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id]);

  useEffect(() => {
    if (hostel?.id) {
      loadFloors();
      loadRooms();
      loadBeds();
    }
  }, [hostel?.id, loadBeds, loadFloors, loadRooms]);

  const handleCreateFloor = async (e: React.FormEvent) => {
    e.preventDefault();
    const fn = newFloor.floorNumber.trim();
    if (!fn) return;
    if (!hostel?.id) {
      toast.error('Please select a hostel before creating a floor.');
      return;
    }
    try {
      await floorService.createFloor({
        hostelId: hostel.id,
        floorNumber: fn,
      });
      setNewFloor({
        hostelId: hostel.id,
        floorNumber: '',
      });
      setShowFloorForm(false);
      loadFloors();
      toast.success('Floor created');
    } catch (error) {
      console.error('Failed to create floor:', error);
      toast.error(errMsg(error, 'Failed to create floor'));
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    const rn = newRoom.roomNumber.trim();
    if (!rn) return;
    try {
      await roomService.createRoom({
        ...newRoom,
        hostelId: hostel!.id,
        floorId: parseInt(newRoom.floorId),
        roomNumber: rn,
        totalBeds: totalBedsFromSharingType(newRoom.sharingType),
        rentPerBed: parseFloat(newRoom.rentPerBed),
      });
      setNewRoom({
        hostelId: hostel!.id,
        floorId: '',
        roomNumber: '',
        roomType: 'NON_AC',
        sharingType: '4_BED',
        totalBeds: '4',
        rentPerBed: '',
      });
      setShowRoomForm(false);
      loadRooms();
      loadBeds();
      toast.success('Room created');
    } catch (error) {
      console.error('Failed to create room:', error);
      toast.error(errMsg(error, 'Failed to create room'));
    }
  };

  const openEditFloor = (f: Floor) => {
    setShowFloorForm(false);
    setEditFloor({
      id: f.id,
      floorNumber: String(f.floorNumber ?? ''),
      description: f.description ?? '',
    });
  };

  const handleSaveFloorEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFloor) return;
    const fn = editFloor.floorNumber.trim();
    if (!fn) return;
    try {
      await floorService.updateFloor(editFloor.id, {
        floorNumber: fn,
        description: editFloor.description.trim() || undefined,
      });
      toast.success('Floor updated');
      setEditFloor(null);
      await Promise.all([loadFloors(), loadBeds()]);
    } catch (error) {
      toast.error(errMsg(error, 'Failed to update floor'));
    }
  };

  const confirmFloorDelete = async () => {
    if (!floorDeleteModal) return;
    setFloorDeleteBusy(true);
    try {
      const data = await floorService.deleteFloor(floorDeleteModal.id);
      const rm = data?.removedRooms ?? 0;
      const rb = data?.removedBeds ?? 0;
      toast.success(data?.message || `Floor removed (${rm} room(s), ${rb} bed(s).)`);
      setFloorDeleteModal(null);
      if (editFloor?.id === floorDeleteModal.id) setEditFloor(null);
      await Promise.all([loadFloors(), loadRooms(), loadBeds()]);
    } catch (error) {
      toast.error(errMsg(error, 'Cannot delete floor'));
    } finally {
      setFloorDeleteBusy(false);
    }
  };

  const isRbFloorSectionOpen = (map: Record<number, boolean>, floorId: number) =>
    map[floorId] !== false;

  const toggleRoomsFloorSection = (floorId: number) => {
    setRoomsByFloorOpen((prev) => ({
      ...prev,
      [floorId]: !isRbFloorSectionOpen(prev, floorId),
    }));
  };

  const toggleBedsFloorSection = (floorId: number) => {
    setBedsByFloorOpen((prev) => ({
      ...prev,
      [floorId]: !isRbFloorSectionOpen(prev, floorId),
    }));
  };

  const bedRoomAccordionKey = (roomId: number) => `room:${roomId}`;
  const bedFloorOrphanBedsKey = (floorId: number) => `floorOrphanBeds:${floorId}`;

  const isBedsRoomAccordionOpen = (map: Record<string, boolean>, key: string) =>
    map[key] !== false;

  const toggleBedsRoomAccordion = (key: string) => {
    setBedsByRoomOpen((prev) => ({
      ...prev,
      [key]: !isBedsRoomAccordionOpen(prev, key),
    }));
  };

  const sortRoomsByLabel = (roomList: Room[]) =>
    [...roomList].sort((a, b) =>
      String(a.roomNumber ?? '').localeCompare(String(b.roomNumber ?? ''), undefined, {
        numeric: true,
      })
    );

  const openEditRoom = (r: Room) => {
    setEditBed(null);
    setShowRoomForm(false);
    setEditRoom({
      id: r.id,
      floorId: String(r.floorId),
      roomNumber: r.roomNumber,
      roomType: normalizeRoomType(r.roomType),
      sharingType: r.sharingType || '4_BED',
      totalBeds: String(totalBedsFromSharingType(r.sharingType || '4_BED')),
      rentPerBed: r.rentPerBed != null ? String(r.rentPerBed) : '',
    });
  };

  const handleSaveRoomEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRoom) return;
    const rn = editRoom.roomNumber.trim();
    if (!rn) return;
    const tb = totalBedsFromSharingType(editRoom.sharingType);
    const rent = parseFloat(editRoom.rentPerBed);
    try {
      await roomService.updateRoom(editRoom.id, {
        floorId: parseInt(editRoom.floorId, 10),
        roomNumber: rn,
        roomType: editRoom.roomType,
        sharingType: editRoom.sharingType,
        totalBeds: tb,
        rentPerBed: Number.isFinite(rent) ? rent : 0,
      });
      toast.success('Room updated');
      setEditRoom(null);
      loadRooms();
      loadBeds();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to update room'));
    }
  };

  const handleDeleteRoom = async (r: Room) => {
    if (roomHasOccupiedBed(r.id)) {
      toast.error('Cannot delete room while any bed is occupied. Move tenants out first.');
      return;
    }
    if (
      !window.confirm(
        `Permanently delete room "${r.roomNumber}"? All non-occupied beds in this room will be deleted as well.`
      )
    ) {
      return;
    }
    try {
      const data = await roomService.deleteRoom(r.id);
      toast.success(data?.message || 'Room removed');
      if (editRoom?.id === r.id) setEditRoom(null);
      loadRooms();
      loadBeds();
      loadFloors();
    } catch (error) {
      toast.error(errMsg(error, 'Cannot delete room'));
    }
  };

  /** Bed list: use rentPerMonth if set; else room’s monthly rent per bed (sharing); else legacy rentPerDay (API used to mirror room rate). */
  const effectiveBedMonthlyRent = (b: Bed): number | null => {
    const explicit = b.rentPerMonth != null ? Number(b.rentPerMonth) : NaN;
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const roomFromList = rooms.find((r) => Number(r.id) === Number(b.roomId));
    if (roomFromList?.rentPerBed != null) {
      const rp = Number(roomFromList.rentPerBed);
      if (Number.isFinite(rp) && rp > 0) return rp;
    }
    if (b.room?.rentPerBed != null) {
      const rp = Number(b.room.rentPerBed);
      if (Number.isFinite(rp) && rp > 0) return rp;
    }
    const legacy = b.rentPerDay != null ? Number(b.rentPerDay) : NaN;
    if (Number.isFinite(legacy) && legacy > 0) return legacy;
    return null;
  };

  const bedMonthlyRentFormDefault = (b: Bed): string => {
    const ex = b.rentPerMonth != null ? Number(b.rentPerMonth) : NaN;
    if (Number.isFinite(ex) && ex > 0) return String(ex);
    const eff = effectiveBedMonthlyRent(b);
    return eff != null ? String(eff) : '';
  };

  const openEditBed = (b: Bed) => {
    setEditRoom(null);
    setEditBed({
      id: b.id,
      bedNumber: b.bedNumber,
      bedName: b.bedName ?? '',
      rentPerWeek: b.rentPerWeek != null ? String(b.rentPerWeek) : '',
      rentPerMonth: bedMonthlyRentFormDefault(b),
    });
  };

  const handleSaveBedEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editBed) return;
    const bn = editBed.bedNumber.trim();
    if (!bn) return;
    const rentMonth = parseFloat(editBed.rentPerMonth);
    if (!Number.isFinite(rentMonth) || rentMonth < 0) {
      toast.error('Enter a valid monthly rent');
      return;
    }
    try {
      await bedService.updateBed(editBed.id, {
        bedNumber: bn,
        bedName: editBed.bedName.trim(),
        rentPerDay: 0,
        rentPerMonth: rentMonth,
        ...(editBed.rentPerWeek.trim()
          ? { rentPerWeek: parseFloat(editBed.rentPerWeek) }
          : {}),
      });
      toast.success('Bed updated');
      setEditBed(null);
      loadBeds();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to update bed'));
    }
  };

  const handleDeleteBed = async (b: Bed) => {
    if (isBedOccupied(b)) {
      toast.error('Cannot delete an occupied bed. Move the tenant out first.');
      return;
    }
    if (!window.confirm(`Permanently delete bed "${b.bedNumber}"?`)) return;
    try {
      await bedService.deleteBed(b.id);
      toast.success('Bed permanently deleted');
      if (editBed?.id === b.id) setEditBed(null);
      loadBeds();
      loadRooms();
      loadFloors();
    } catch (error) {
      toast.error(errMsg(error, 'Cannot delete bed'));
    }
  };

  const sortedFloors = [...floors].sort((a, b) =>
    String(a.floorNumber ?? '').localeCompare(String(b.floorNumber ?? ''), undefined, {
      numeric: true,
    })
  );

  const orphanRooms = rooms.filter(
    (r) => !floors.some((f) => Number(f.id) === Number(r.floorId))
  );

  const orphanBeds = beds.filter(
    (b) => !floors.some((f) => bedsOnFloor(f.id).some((x) => x.id === b.id))
  );

  const renderRoomGridCard = (room: Room) => {
    return (
      <div
        key={room.id}
        className={`list-item rb-grid-card rb-grid-card-static${
          editRoom?.id === room.id ? ' rb-grid-card-editing' : ''
        }`}
      >
        <div className="item-header">
          <div className="item-header-main rb-grid-card-head-main">
            <h3>Room {room.roomNumber}</h3>
            <span className="badge">{room.totalBeds} beds</span>
            <span
              className={`badge ${
                (room.occupiedBeds ?? 0) > 0 ? 'occupied' : 'available'
              }`}
            >
              {room.occupiedBeds ?? 0} occupied
            </span>
          </div>
          <div className="item-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => openEditRoom(room)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => handleDeleteRoom(room)}
              disabled={roomHasOccupiedBed(room.id)}
              title={
                roomHasOccupiedBed(room.id)
                  ? 'Vacate occupied beds in this room before deleting.'
                  : undefined
              }
            >
              Delete
            </button>
          </div>
        </div>
        <div className="rb-grid-card-detail rb-grid-card-detail-static rb-room-card-meta">
          <span
            className={`rb-room-type-badge ${
              normalizeRoomType(room.roomType) === 'AC'
                ? 'rb-room-type-ac'
                : 'rb-room-type-non-ac'
            }`}
          >
            {formatRoomTypeLabel(room.roomType)}
          </span>
          <span className="rb-room-rent-highlight" title="Monthly rent per bed">
            ₹{room.rentPerBed ?? '—'}
            <span className="rb-room-rent-suffix"> / bed · month</span>
          </span>
        </div>

        {editRoom?.id === room.id && (
          <form className="form-container item-edit-form" onSubmit={handleSaveRoomEdit}>
            <div className="form-row">
              <div className="form-group">
                <label>Floor *</label>
                <select
                  required
                  value={editRoom.floorId}
                  onChange={(e) => setEditRoom({ ...editRoom, floorId: e.target.value })}
                >
                  {floors.map((f) => (
                    <option key={f.id} value={f.id}>
                      Floor {f.floorNumber}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Room number / label *</label>
                <input
                  type="text"
                  required
                  value={editRoom.roomNumber}
                  onChange={(e) =>
                    setEditRoom({ ...editRoom, roomNumber: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Room type *</label>
                <select
                  required
                  value={editRoom.roomType}
                  onChange={(e) =>
                    setEditRoom({
                      ...editRoom,
                      roomType: e.target.value as NonNullable<Room['roomType']>,
                    })
                  }
                >
                  <option value="AC">AC</option>
                  <option value="NON_AC">Non-AC</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sharing type *</label>
                <select
                  required
                  value={editRoom.sharingType}
                  onChange={(e) => {
                    const st = e.target.value;
                    const occ = room.occupiedBeds ?? 0;
                    const derived = totalBedsFromSharingType(st);
                    if (derived < occ) {
                      toast.error(
                        `This sharing allows ${derived} bed(s), but ${occ} are occupied.`
                      );
                      return;
                    }
                    setEditRoom({
                      ...editRoom,
                      sharingType: st,
                      totalBeds: String(derived),
                    });
                  }}
                >
                  <option value="PRIVATE">Private (1 bed)</option>
                  <option value="2_BED">2 sharing (2 beds)</option>
                  <option value="3_BED">3 sharing (3 beds)</option>
                  <option value="4_BED">4 sharing (4 beds)</option>
                  <option value="6_BED">6 sharing (6 beds)</option>
                  <option value="8_BED">8 sharing (8 beds)</option>
                </select>
                <small className="form-hint">Total beds follow sharing type.</small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Total beds</label>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={totalBedsFromSharingType(editRoom.sharingType)}
                />
                <small className="form-hint">
                  Occupied: {room.occupiedBeds ?? 0}. Cannot pick a sharing with fewer beds than
                  occupied.
                </small>
              </div>
              <div className="form-group">
                <label>Monthly rent per bed (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={editRoom.rentPerBed}
                  onChange={(e) =>
                    setEditRoom({ ...editRoom, rentPerBed: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <button type="submit" className="btn btn-success btn-sm">
                Save room
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditRoom(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderBedGridCard = (bed: Bed) => {
    const occupant = bedOccupantInfo(bed);
    return (
      <div
        key={bed.id}
        className={`list-item rb-grid-card rb-grid-card-static${
          editBed?.id === bed.id ? ' rb-grid-card-editing' : ''
        }`}
      >
        <div className="item-header">
          <div className="item-header-main rb-grid-card-head-main">
            <h3>Bed {bed.bedNumber}</h3>
            <span className={`badge ${bed.bedStatus?.toLowerCase()}`}>{bed.bedStatus}</span>
          </div>
          <div className="item-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => openEditBed(bed)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => handleDeleteBed(bed)}
              disabled={isBedOccupied(bed)}
              title={
                isBedOccupied(bed)
                  ? 'Occupied beds cannot be deleted. Move the tenant out first.'
                  : undefined
              }
            >
              Delete
            </button>
          </div>
        </div>
        <div className="rb-grid-card-detail rb-grid-card-detail-static rb-bed-card-meta">
          <span className="rb-bed-rent-highlight" title="Monthly rent">
            ₹{effectiveBedMonthlyRent(bed) ?? '—'}
            <span className="rb-bed-rent-suffix"> / month</span>
          </span>
          {occupant ? (
            <span className="rb-bed-occupant">
              <span className="rb-bed-occupant-label">Tenant</span>
              <TenantNameLink userId={occupant.id} name={occupant.name} className="rb-bed-occupant-link" />
            </span>
          ) : null}
        </div>

        {editBed?.id === bed.id && (
          <form className="form-container item-edit-form" onSubmit={handleSaveBedEdit}>
            <div className="form-row">
              <div className="form-group">
                <label>Bed number / label *</label>
                <input
                  type="text"
                  required
                  value={editBed.bedNumber}
                  onChange={(e) =>
                    setEditBed({ ...editBed, bedNumber: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Bed name</label>
                <input
                  type="text"
                  value={editBed.bedName}
                  onChange={(e) => setEditBed({ ...editBed, bedName: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Rent per month (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={editBed.rentPerMonth}
                  onChange={(e) =>
                    setEditBed({ ...editBed, rentPerMonth: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Rent per week (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editBed.rentPerWeek}
                  onChange={(e) =>
                    setEditBed({ ...editBed, rentPerWeek: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <button type="submit" className="btn btn-success btn-sm">
                Save bed
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditBed(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderBedsNestedByRoomForFloor = (floorId: number, floorBeds: Bed[]) => {
    const floorRooms = sortRoomsByLabel(roomsOnFloor(floorId));
    const roomIdsOnFloor = new Set(floorRooms.map((r) => Number(r.id)));
    const byRoom = new Map<number, Bed[]>();
    for (const b of floorBeds) {
      const rid = Number(b.roomId);
      if (!byRoom.has(rid)) byRoom.set(rid, []);
      byRoom.get(rid)!.push(b);
    }

    const sections: React.ReactNode[] = [];

    for (const room of floorRooms) {
      const roomBeds = byRoom.get(Number(room.id));
      if (!roomBeds?.length) continue;
      const key = bedRoomAccordionKey(room.id);
      const roomOpen = isBedsRoomAccordionOpen(bedsByRoomOpen, key);
      const occ = roomBeds.filter((b) => isBedActive(b) && isBedOccupied(b)).length;
      sections.push(
        <section key={key} className="rb-room-block">
          <div
            className="rb-room-block-head"
            role="button"
            tabIndex={0}
            aria-expanded={roomOpen}
            aria-label={`${roomOpen ? 'Collapse' : 'Expand'} room ${room.roomNumber}`}
            onClick={() => toggleBedsRoomAccordion(key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleBedsRoomAccordion(key);
              }
            }}
          >
            <span className="rb-grid-chevron rb-grid-chevron-nested" aria-hidden>
              {roomOpen ? '▼' : '▶'}
            </span>
            <div className="rb-room-block-title">
              <h4 className="rb-room-block-heading">Room {room.roomNumber}</h4>
            </div>
            <span className="badge">{roomBeds.length} bed(s)</span>
            <span className={`badge ${occ > 0 ? 'occupied' : 'available'}`}>{occ} occupied</span>
          </div>
          {roomOpen && (
            <div className="list-container rb-entity-grid rb-room-block-beds">
              {roomBeds.map((bed) => renderBedGridCard(bed))}
            </div>
          )}
        </section>
      );
    }

    const stray = floorBeds.filter((b) => !roomIdsOnFloor.has(Number(b.roomId)));
    if (stray.length > 0) {
      const ok = bedFloorOrphanBedsKey(floorId);
      const open = isBedsRoomAccordionOpen(bedsByRoomOpen, ok);
      const occ = stray.filter((b) => isBedActive(b) && isBedOccupied(b)).length;
      sections.push(
        <section key={ok} className="rb-room-block rb-room-block-stray">
          <div
            className="rb-room-block-head"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} other beds on this floor`}
            onClick={() => toggleBedsRoomAccordion(ok)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleBedsRoomAccordion(ok);
              }
            }}
          >
            <span className="rb-grid-chevron rb-grid-chevron-nested" aria-hidden>
              {open ? '▼' : '▶'}
            </span>
            <div className="rb-room-block-title">
              <h4 className="rb-room-block-heading">Other beds on this floor</h4>
              <span className="rb-room-block-sub">Not linked to a room in this list</span>
            </div>
            <span className="badge">{stray.length} bed(s)</span>
            <span className={`badge ${occ > 0 ? 'occupied' : 'available'}`}>{occ} occupied</span>
          </div>
          {open && (
            <div className="list-container rb-entity-grid rb-room-block-beds">
              {stray.map((bed) => renderBedGridCard(bed))}
            </div>
          )}
        </section>
      );
    }

    if (sections.length === 0) {
      return <p className="rb-floor-empty">No beds on this floor yet.</p>;
    }

    return <div className="rb-floor-rooms-nested">{sections}</div>;
  };

  const renderBedsNestedByRoomOrphanFloorSection = () => {
    if (orphanBeds.length === 0) {
      return <p className="rb-floor-empty">No beds in this section.</p>;
    }
    const byRoom = new Map<number, Bed[]>();
    for (const b of orphanBeds) {
      const rid = Number(b.roomId);
      if (!byRoom.has(rid)) byRoom.set(rid, []);
      byRoom.get(rid)!.push(b);
    }
    const roomIds = Array.from(byRoom.keys()).sort((a, c) => {
      const ra = rooms.find((r) => Number(r.id) === a);
      const rc = rooms.find((r) => Number(r.id) === c);
      return String(ra?.roomNumber ?? a).localeCompare(String(rc?.roomNumber ?? c), undefined, {
        numeric: true,
      });
    });
    const sections: React.ReactNode[] = [];

    for (const rid of roomIds) {
      const roomBeds = byRoom.get(rid)!;
      const room = rooms.find((r) => Number(r.id) === rid);
      const key = bedRoomAccordionKey(rid);
      const open = isBedsRoomAccordionOpen(bedsByRoomOpen, key);
      const occ = roomBeds.filter((b) => isBedActive(b) && isBedOccupied(b)).length;
      const label = room != null ? `Room ${room.roomNumber}` : `Room #${rid}`;
      sections.push(
        <section key={key} className="rb-room-block">
          <div
            className="rb-room-block-head"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
            onClick={() => toggleBedsRoomAccordion(key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleBedsRoomAccordion(key);
              }
            }}
          >
            <span className="rb-grid-chevron rb-grid-chevron-nested" aria-hidden>
              {open ? '▼' : '▶'}
            </span>
            <div className="rb-room-block-title">
              <h4 className="rb-room-block-heading">{label}</h4>
              {room == null ? (
                <span className="rb-room-block-sub">Room not in floor listing above</span>
              ) : null}
            </div>
            <span className="badge">{roomBeds.length} bed(s)</span>
            <span className={`badge ${occ > 0 ? 'occupied' : 'available'}`}>{occ} occupied</span>
          </div>
          {open && (
            <div className="list-container rb-entity-grid rb-room-block-beds">
              {roomBeds.map((bed) => renderBedGridCard(bed))}
            </div>
          )}
        </section>
      );
    }

    return <div className="rb-floor-rooms-nested rb-floor-rooms-nested-orphan">{sections}</div>;
  };

  if (loading || !hostel) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <h1>Property Management</h1>

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'floors' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('floors');
          }}
        >
          Floors ({floors.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('rooms');
          }}
        >
          Rooms ({rooms.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'beds' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('beds');
          }}
        >
          Beds ({beds.length})
        </button>
      </div>

      {activeTab === 'floors' && (
        <div className="tab-content">
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditFloor(null);
              setShowFloorForm(!showFloorForm);
            }}
          >
            {showFloorForm ? 'Cancel' : '+ Add Floor'}
          </button>

          {showFloorForm && (
            <form onSubmit={handleCreateFloor} className="form-container">
              <div className="form-row">
                <div className="form-group">
                  <label>Floor number / label *</label>
                  <input
                    type="text"
                    required
                    value={newFloor.floorNumber}
                    placeholder="e.g. G, 1, 2A, Mezzanine"
                    onChange={(e) =>
                      setNewFloor({
                        ...newFloor,
                        floorNumber: e.target.value,
                      })
                    }
                  />
                  <small className="form-hint">Any label unique within this property (not limited to digits).</small>
                </div>
              </div>
              <button type="submit" className="btn btn-success">
                Create Floor
              </button>
            </form>
          )}

          <div className="list-container">
            {floors.map((floor) => (
              <div key={floor.id} className="list-item">
                <div className="item-header">
                  <div className="item-header-main">
                    <h3>Floor {floor.floorNumber}</h3>
                    <span className="badge">{floor.totalRooms ?? 0} rooms</span>
                  </div>
                  <div className="item-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => openEditFloor(floor)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setFloorDeleteModal(floor)}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {editFloor?.id === floor.id && (
                  <form className="form-container item-edit-form" onSubmit={handleSaveFloorEdit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Floor number / label *</label>
                        <input
                          type="text"
                          required
                          value={editFloor.floorNumber}
                          onChange={(e) =>
                            setEditFloor({ ...editFloor, floorNumber: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        rows={2}
                        value={editFloor.description}
                        onChange={(e) =>
                          setEditFloor({ ...editFloor, description: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-row">
                      <button type="submit" className="btn btn-success btn-sm">
                        Save floor
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditFloor(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rooms' && (
        <div className="tab-content">
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditRoom(null);
              const next = !showRoomForm;
              setShowRoomForm(next);
            }}
          >
            {showRoomForm ? 'Cancel' : '+ Add Room'}
          </button>

          {showRoomForm && (
            <form onSubmit={handleCreateRoom} className="form-container">
              <div className="form-row">
                <div className="form-group">
                  <label>Floor *</label>
                  <select
                    required
                    value={newRoom.floorId}
                    onChange={(e) =>
                      setNewRoom({
                        ...newRoom,
                        floorId: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Floor</option>
                    {floors.map((floor) => (
                      <option key={floor.id} value={floor.id}>
                        Floor {floor.floorNumber}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Room number / label *</label>
                  <input
                    type="text"
                    required
                    value={newRoom.roomNumber}
                    placeholder="e.g. 101, A-12, Suite-1"
                    onChange={(e) =>
                      setNewRoom({
                        ...newRoom,
                        roomNumber: e.target.value,
                      })
                    }
                  />
                  <small className="form-hint">Unique within this property; letters and numbers allowed.</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Room Type *</label>
                  <select
                    required
                    value={newRoom.roomType}
                    onChange={(e) =>
                      setNewRoom({
                        ...newRoom,
                        roomType: e.target.value as NonNullable<Room['roomType']>,
                      })
                    }
                  >
                    <option value="AC">AC</option>
                    <option value="NON_AC">Non-AC</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sharing type *</label>
                  <select
                    required
                    value={newRoom.sharingType}
                    onChange={(e) => {
                      const st = e.target.value;
                      setNewRoom({
                        ...newRoom,
                        sharingType: st,
                        totalBeds: String(totalBedsFromSharingType(st)),
                      });
                    }}
                  >
                    <option value="PRIVATE">Private (1 bed)</option>
                    <option value="2_BED">2 sharing (2 beds)</option>
                    <option value="3_BED">3 sharing (3 beds)</option>
                    <option value="4_BED">4 sharing (4 beds)</option>
                    <option value="6_BED">6 sharing (6 beds)</option>
                    <option value="8_BED">8 sharing (8 beds)</option>
                  </select>
                  <small className="form-hint">Total beds follow sharing type automatically.</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Total beds</label>
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={totalBedsFromSharingType(newRoom.sharingType)}
                  />
                </div>
                <div className="form-group">
                  <label>Monthly rent per bed (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newRoom.rentPerBed}
                    onChange={(e) =>
                      setNewRoom({
                        ...newRoom,
                        rentPerBed: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-success">
                Create Room
              </button>
            </form>
          )}

          <div className="rb-floor-groups">
            {sortedFloors.length === 0 && orphanRooms.length === 0 && (
              <p className="rb-floor-empty">Add floors in the Floors tab to organize rooms.</p>
            )}
            {sortedFloors.map((floor) => {
              const floorRooms = roomsOnFloor(floor.id);
              const floorSectionOpen = isRbFloorSectionOpen(roomsByFloorOpen, floor.id);
              return (
                <section key={floor.id} className="rb-floor-block">
                  <div
                    className="rb-floor-block-head"
                    role="button"
                    tabIndex={0}
                    aria-expanded={floorSectionOpen}
                    aria-label={`${floorSectionOpen ? 'Collapse' : 'Expand'} floor ${floor.floorNumber}`}
                    onClick={() => toggleRoomsFloorSection(floor.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRoomsFloorSection(floor.id);
                      }
                    }}
                  >
                    <span className="rb-grid-chevron" aria-hidden>
                      {floorSectionOpen ? '▼' : '▶'}
                    </span>
                    <div className="rb-floor-block-title">
                      <h3 className="rb-floor-block-heading">Floor {floor.floorNumber}</h3>
                    </div>
                    <span className="badge">{floorRooms.length} room(s)</span>
                  </div>
                  {floorSectionOpen &&
                    (floorRooms.length === 0 ? (
                      <p className="rb-floor-empty">No rooms on this floor yet.</p>
                    ) : (
                      <div className="list-container rb-entity-grid">
                        {floorRooms.map((room) => renderRoomGridCard(room))}
                      </div>
                    ))}
                </section>
              );
            })}
            {orphanRooms.length > 0 && (
              <section className="rb-floor-block rb-floor-block-orphan">
                <div
                  className="rb-floor-block-head"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isRbFloorSectionOpen(
                    roomsByFloorOpen,
                    ORPHAN_FLOOR_SECTION_ID
                  )}
                  aria-label="Expand or collapse rooms not linked to a listed floor"
                  onClick={() => toggleRoomsFloorSection(ORPHAN_FLOOR_SECTION_ID)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRoomsFloorSection(ORPHAN_FLOOR_SECTION_ID);
                    }
                  }}
                >
                  <span className="rb-grid-chevron" aria-hidden>
                    {isRbFloorSectionOpen(roomsByFloorOpen, ORPHAN_FLOOR_SECTION_ID)
                      ? '▼'
                      : '▶'}
                  </span>
                  <div className="rb-floor-block-title">
                    <h3 className="rb-floor-block-heading">Other</h3>
                    <span className="rb-floor-block-sub">
                      Rooms not linked to a floor in this list
                    </span>
                  </div>
                  <span className="badge">{orphanRooms.length} room(s)</span>
                </div>
                {isRbFloorSectionOpen(roomsByFloorOpen, ORPHAN_FLOOR_SECTION_ID) && (
                  <div className="list-container rb-entity-grid">
                    {orphanRooms.map((room) => renderRoomGridCard(room))}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      )}

      {activeTab === 'beds' && (
        <div className="tab-content">
          <div className="form-container">
            <p className="muted">
              Bed creation is disabled. Beds are created from Rooms (based on sharing type) and managed here.
            </p>
          </div>

          <div className="rb-floor-groups">
            {sortedFloors.length === 0 && orphanBeds.length === 0 && (
              <p className="rb-floor-empty">Add floors in the Floors tab to organize beds.</p>
            )}
            {sortedFloors.map((floor) => {
              const floorBeds = bedsOnFloor(floor.id);
              const floorSectionOpen = isRbFloorSectionOpen(bedsByFloorOpen, floor.id);
              const occ = occupiedBedsOnFloor(floor.id);
              return (
                <section key={floor.id} className="rb-floor-block">
                  <div
                    className="rb-floor-block-head"
                    role="button"
                    tabIndex={0}
                    aria-expanded={floorSectionOpen}
                    aria-label={`${floorSectionOpen ? 'Collapse' : 'Expand'} floor ${floor.floorNumber}`}
                    onClick={() => toggleBedsFloorSection(floor.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleBedsFloorSection(floor.id);
                      }
                    }}
                  >
                    <span className="rb-grid-chevron" aria-hidden>
                      {floorSectionOpen ? '▼' : '▶'}
                    </span>
                    <div className="rb-floor-block-title">
                      <h3 className="rb-floor-block-heading">Floor {floor.floorNumber}</h3>
                    </div>
                    <span className="badge">{floorBeds.length} bed(s)</span>
                    <span className={`badge ${occ > 0 ? 'occupied' : 'available'}`}>
                      {occ} occupied
                    </span>
                  </div>
                  {floorSectionOpen &&
                    (floorBeds.length === 0 ? (
                      <p className="rb-floor-empty">No beds on this floor yet.</p>
                    ) : (
                      <div className="rb-floor-rooms-wrap">
                        {renderBedsNestedByRoomForFloor(floor.id, floorBeds)}
                      </div>
                    ))}
                </section>
              );
            })}
            {orphanBeds.length > 0 && (
              <section className="rb-floor-block rb-floor-block-orphan">
                <div
                  className="rb-floor-block-head"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isRbFloorSectionOpen(
                    bedsByFloorOpen,
                    ORPHAN_FLOOR_SECTION_ID
                  )}
                  aria-label="Expand or collapse beds not linked to a listed floor"
                  onClick={() => toggleBedsFloorSection(ORPHAN_FLOOR_SECTION_ID)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleBedsFloorSection(ORPHAN_FLOOR_SECTION_ID);
                    }
                  }}
                >
                  <span className="rb-grid-chevron" aria-hidden>
                    {isRbFloorSectionOpen(bedsByFloorOpen, ORPHAN_FLOOR_SECTION_ID)
                      ? '▼'
                      : '▶'}
                  </span>
                  <div className="rb-floor-block-title">
                    <h3 className="rb-floor-block-heading">Other</h3>
                    <span className="rb-floor-block-sub">
                      Beds not linked to a floor in this list
                    </span>
                  </div>
                  <span className="badge">{orphanBeds.length} bed(s)</span>
                </div>
                {isRbFloorSectionOpen(bedsByFloorOpen, ORPHAN_FLOOR_SECTION_ID) && (
                  <div className="rb-floor-rooms-wrap">
                    {renderBedsNestedByRoomOrphanFloorSection()}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      )}

      {floorDeleteModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-floor-title"
          onClick={() => !floorDeleteBusy && setFloorDeleteModal(null)}
        >
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="delete-floor-title">Delete floor</h2>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                disabled={floorDeleteBusy}
                onClick={() => setFloorDeleteModal(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>Floor {floorDeleteModal.floorNumber}</strong>
              </p>
              <p className="modal-hint">
                This will <strong>permanently delete</strong> the floor and all rooms and beds on
                this floor. This cannot be undone from the UI.
              </p>
              <ul style={{ margin: '0.75rem 0', paddingLeft: '1.25rem', color: '#475569' }}>
                <li>{roomsOnFloor(floorDeleteModal.id).length} room(s)</li>
                <li>{bedsOnFloor(floorDeleteModal.id).length} bed(s)</li>
                <li>{occupiedBedsOnFloor(floorDeleteModal.id)} occupied bed(s)</li>
              </ul>
              {occupiedBedsOnFloor(floorDeleteModal.id) > 0 ? (
                <p className="form-hint" style={{ color: '#b45309' }}>
                  Move tenants out of occupied beds on this floor before you can delete it.
                </p>
              ) : (
                <p className="form-hint">Confirm only if you intend to remove this entire floor.</p>
              )}
            </div>
            <div className="modal-footer-btns">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={floorDeleteBusy}
                onClick={() => setFloorDeleteModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={floorDeleteBusy || occupiedBedsOnFloor(floorDeleteModal.id) > 0}
                onClick={confirmFloorDelete}
              >
                {floorDeleteBusy ? 'Deleting…' : 'Delete floor & contents'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBedManagement;
